/**
 * Универсальная утилита для обогащения MST snapshots вычисляемыми полями
 * Использует схему для определения calculated полей и добавляет их значения из живых MST экземпляров
 */

import { getSnapshot } from 'mobx-state-tree';

/**
 * Обогащает snapshot MST экземпляра вычисляемыми полями на основе схемы
 * @param {Object} mstInstance - Живой MST экземпляр (с views)
 * @param {Object} schemaProperties - Properties из JSON Schema
 * @returns {Object} Обогащенный snapshot с вычисляемыми полями
 */
export function getEnrichedSnapshot(mstInstance, schemaProperties) {
    if (!mstInstance) return null;
    if (!schemaProperties) return getSnapshot(mstInstance);

    // Получаем базовый snapshot (без views)
    const baseSnapshot = getSnapshot(mstInstance);

    // Обогащаем рекурсивно
    return enrichObject(baseSnapshot, mstInstance, schemaProperties);
}

/**
 * Рекурсивно обогащает объект вычисляемыми полями
 * @param {Object} snapshot - Snapshot объекта (без views)
 * @param {Object} liveInstance - Живой MST экземпляр (с views)
 * @param {Object} schemaProps - Properties из схемы для этого уровня
 * @returns {Object} Обогащенный объект
 */
function enrichObject(snapshot, liveInstance, schemaProps) {
    if (!snapshot || !liveInstance || !schemaProps) return snapshot;

    const enriched = { ...snapshot };

    // Проходим по всем полям в схеме
    Object.entries(schemaProps).forEach(([fieldName, fieldSchema]) => {
        // 1. Если поле вычисляемое - берем из живого экземпляра
        if (fieldSchema['x-calculated']) {
            if (fieldName in liveInstance) {
                enriched[fieldName] = liveInstance[fieldName];
            }
            return;
        }

        // 2. Если поле - массив, обрабатываем рекурсивно
        if (fieldSchema.type === 'array' && fieldSchema.items?.properties) {
            if (Array.isArray(liveInstance[fieldName]) && Array.isArray(snapshot[fieldName])) {
                enriched[fieldName] = liveInstance[fieldName].map((liveItem, index) => {
                    const snapshotItem = snapshot[fieldName][index];
                    if (!snapshotItem) return getSnapshot(liveItem);

                    return enrichObject(snapshotItem, liveItem, fieldSchema.items.properties);
                });
            }
            return;
        }

        // 3. Если поле - объект с properties, обрабатываем рекурсивно
        if (fieldSchema.type === 'object' && fieldSchema.properties) {
            if (liveInstance[fieldName]) {
                const objSnapshot = snapshot[fieldName] || getSnapshot(liveInstance[fieldName]);
                enriched[fieldName] = enrichObject(
                    objSnapshot,
                    liveInstance[fieldName],
                    fieldSchema.properties
                );
            }
            return;
        }

        // 4. Остальные поля уже есть в snapshot - пропускаем
    });

    return enriched;
}
